import { AiStatus } from '../entity/api/ai';
import { ExportKind } from '../entity/api/export';
import {
  type ArticleSortAndFilter,
  type CreateArticleReq,
  type CreateArticleRes,
  type ExportArticleIntegrationReq,
  type ExportArticleIntegrationRes,
  type ExportArticleRes,
  type GenerateOneClickReq,
  type GenerateOneClickRes,
  type GeneratePrimaryKeywordReq,
  type GeneratePrimaryKeywordRes,
  type GeneratePrimaryKeywordSocketRes,
  type GeneratePrimaryPromptReq,
  type GeneratePrimaryPromptRes,
  type GeneratePrimaryPromptSocketRes,
  type GetArticleByIdRes,
  type GetArticlesByBrandIdRes,
  type GetArticlesByOrgIdRes,
  type RefreshContentReq,
  type RefreshContentRes,
  type SaveArticleReq,
  type SaveArticleRes,
  type SaveArticleUserRatingReq,
  type SaveArticleUserRatingRes,
  createArticleReqSchema,
  delArticleReqSchema,
  exportArticleIntegrationReqSchema,
  exportArticleReqSchema,
  generateOneClickReqSchema,
  generatePrimaryKeywordReqSchema,
  generatePrimaryKeywordSocketResSchema,
  generatePrimaryPromptReqSchema,
  generatePrimaryPromptSocketResSchema,
  refreshContentReqSchema,
  saveArticleReqSchema,
  saveArticleUserRatingReqSchema,
} from '../entity/api/routes/article';
import { InternalServerError } from '../error';
import { dasherize } from '../shared/string';
import { apiRequest, setURL } from './request';

// NOTE: We Store pending promises for keyword generation
const pendingKeywordGenerations = new Map<
  string,
  {
    resolve: (value: GeneratePrimaryKeywordSocketRes) => void;
    reject: (reason?: unknown) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }
>();

// NOTE: We Store pending promises for prompt generation
const pendingPromptGenerations = new Map<
  string,
  {
    resolve: (value: GeneratePrimaryPromptSocketRes) => void;
    reject: (reason?: unknown) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }
>();

const DEFAULT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

function buildPaginationQueryString(params?: ArticleSortAndFilter): string {
  if (params == null) {
    return '';
  }

  const searchParams = new URLSearchParams();

  const { search, offset, limit, createdBy, sortBy, sortByDir } = params;

  if (search != null && search.trim() !== '') {
    searchParams.set('search', search.trim());
  }

  if (offset != null) {
    searchParams.set('offset', String(offset));
  }

  if (limit != null) {
    searchParams.set('limit', String(limit));
  }

  if (createdBy != null && createdBy.length > 0) {
    for (const userId of createdBy) {
      searchParams.append('createdBy[]', userId);
    }
  }

  if (sortBy != null) {
    searchParams.set('sortBy', sortBy);
  }

  if (sortByDir != null) {
    searchParams.set('sortByDir', sortByDir);
  }

  const queryString = searchParams.toString();
  return queryString.length > 0 ? `?${queryString}` : '';
}

export function getArticleById(id: string) {
  return apiRequest<GetArticleByIdRes>(`/article/${id}`, 'GET', true);
}

export function getArticlesByOrgId(organizationId: string, queryParams?: ArticleSortAndFilter) {
  const queryString = buildPaginationQueryString(queryParams);
  const url = `/article/get-by-org/${organizationId}${queryString}`;
  return apiRequest<GetArticlesByOrgIdRes>(url, 'GET', true);
}

export function getArticlesByBrandId(brandId: string, queryParams?: ArticleSortAndFilter) {
  const queryString = buildPaginationQueryString(queryParams);
  const url = `/article/get-by-brand/${brandId}${queryString}`;
  return apiRequest<GetArticlesByBrandIdRes>(url, 'GET', true);
}

export async function createArticle(data: CreateArticleReq['body']) {
  const req = createArticleReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  return apiRequest<CreateArticleRes>(`/article`, 'POST', true, req.body);
}

export async function updateArticleById(
  id: SaveArticleReq['params']['id'],
  data: SaveArticleReq['body']
) {
  const req = saveArticleReqSchema.parse({
    params: { id },
    query: null,
    body: data,
  });
  return apiRequest<SaveArticleRes>(`/article/${req.params.id}`, 'PATCH', true, req.body);
}

export async function saveArticleUserRating(
  id: SaveArticleUserRatingReq['params']['id'],
  data: SaveArticleUserRatingReq['body']
) {
  const req = saveArticleUserRatingReqSchema.parse({
    params: { id },
    query: null,
    body: data,
  });
  return apiRequest<SaveArticleUserRatingRes>(
    `/article/${req.params.id}/rating`,
    'PATCH',
    true,
    req.body
  );
}

export async function removeArticleById(id: string) {
  const req = delArticleReqSchema.parse({
    params: { id },
    query: null,
    body: null,
  });

  await apiRequest<unknown>(`/article/${req.params.id}`, 'DELETE', true, req.body);
}

// NOTE: We handle the socket event when the keyword generation is completed
export function onArticleGeneratePrimaryKeywordUpdate(data: GeneratePrimaryKeywordSocketRes) {
  const parsedData = generatePrimaryKeywordSocketResSchema.parse(data);
  const { contentId, status } = parsedData;
  const pending = pendingKeywordGenerations.get(contentId);

  if (pending != null) {
    if (status === AiStatus.Success) {
      clearTimeout(pending.timeoutId);
      pending.resolve(data);
      pendingKeywordGenerations.delete(contentId);
    } else if (status === AiStatus.Error) {
      clearTimeout(pending.timeoutId);
      pending.reject(new InternalServerError(data.error ?? 'Keyword generation failed'));
      pendingKeywordGenerations.delete(contentId);
    }
    // For 'pending' or 'cancelled' status, we keep waiting (we don't resolve or reject yet)
  }
}

// NOTE: We handle the socket event when the prompt generation is completed
export function onArticleGeneratePrimaryPromptUpdate(data: GeneratePrimaryPromptSocketRes) {
  const parsedData = generatePrimaryPromptSocketResSchema.parse(data);
  const { contentId, status } = parsedData;
  const pending = pendingPromptGenerations.get(contentId);

  if (pending != null) {
    if (status === AiStatus.Success) {
      clearTimeout(pending.timeoutId);
      pending.resolve(data);
      pendingPromptGenerations.delete(contentId);
    } else if (status === AiStatus.Error) {
      clearTimeout(pending.timeoutId);
      pending.reject(new InternalServerError(data.error ?? 'Prompt generation failed'));
      pendingPromptGenerations.delete(contentId);
    }
    // For 'pending' or 'cancelled' status, we keep waiting (we don't resolve or reject yet)
  }
}

export function cancelPromptGeneration(contentId: string) {
  const pending = pendingPromptGenerations.get(contentId);
  if (pending != null) {
    clearTimeout(pending.timeoutId);
    pending.reject(new InternalServerError('Prompt generation cancelled'));
    pendingPromptGenerations.delete(contentId);
  }
}

export function cancelKeywordGeneration(contentId: string) {
  const pending = pendingKeywordGenerations.get(contentId);
  if (pending != null) {
    clearTimeout(pending.timeoutId);
    pending.reject(new InternalServerError('Keyword generation cancelled'));
    pendingKeywordGenerations.delete(contentId);
  }
}

export function generateKeyword(
  data: GeneratePrimaryKeywordReq['body']
): Promise<GeneratePrimaryKeywordSocketRes> {
  const req = generatePrimaryKeywordReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  const url = setURL('/article/generate-primary-keyword', req.query ?? undefined);

  // We create a promise that will be resolved when the socket event arrives
  const socketPromise = new Promise<GeneratePrimaryKeywordSocketRes>(function (resolve, reject) {
    // NOTE: We timeout after 3 minutes if no response
    const timeoutId = setTimeout(function () {
      const pending = pendingKeywordGenerations.get(req.body.brandId);
      if (pending != null) {
        pending.reject(
          new InternalServerError(
            `Keyword generation timed out after ${DEFAULT_TIMEOUT_MS / 1000} seconds`
          )
        );
        pendingKeywordGenerations.delete(req.body.brandId);
      }
    }, DEFAULT_TIMEOUT_MS);

    // NOTE: Store the promise handlers and timeout ID together
    pendingKeywordGenerations.set(req.body.brandId, {
      resolve: function (value) {
        clearTimeout(timeoutId);
        resolve(value);
      },
      reject: function (reason: unknown) {
        clearTimeout(timeoutId);
        reject(reason instanceof Error ? reason : new Error(String(reason)));
      },
      timeoutId,
    });
  });

  // We launch the HTTP request (don't await it)
  apiRequest<GeneratePrimaryKeywordRes>(url, 'POST', true, req.body).catch(function (err) {
    // If the HTTP request fails, we reject the socket promise and clear the timeout
    const pending = pendingKeywordGenerations.get(req.body.brandId);
    if (pending != null) {
      clearTimeout(pending.timeoutId);
      pending.reject(err);
      pendingKeywordGenerations.delete(req.body.brandId);
    }
  });

  // We return the promise that will resolve when the socket event arrives
  return socketPromise;
}

export function generatePrimaryPrompt(
  data: GeneratePrimaryPromptReq['body']
): Promise<GeneratePrimaryPromptSocketRes> {
  const req = generatePrimaryPromptReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  const url = setURL('/article/generate-primary-prompt', req.query ?? undefined);

  // We create a promise that will be resolved when the socket event arrives
  const socketPromise = new Promise<GeneratePrimaryPromptSocketRes>(function (resolve, reject) {
    // NOTE: We timeout after 3 minutes if no response
    const timeoutId = setTimeout(function () {
      const pending = pendingPromptGenerations.get(req.body.brandId);
      if (pending != null) {
        pending.reject(
          new InternalServerError(
            `Prompt generation timed out after ${DEFAULT_TIMEOUT_MS / 1000} seconds`
          )
        );
        pendingPromptGenerations.delete(req.body.brandId);
      }
    }, DEFAULT_TIMEOUT_MS);

    // NOTE: Store the promise handlers and timeout ID together
    pendingPromptGenerations.set(req.body.brandId, {
      resolve: function (value) {
        clearTimeout(timeoutId);
        resolve(value);
      },
      reject: function (reason: unknown) {
        clearTimeout(timeoutId);
        reject(reason instanceof Error ? reason : new Error(String(reason)));
      },
      timeoutId,
    });
  });

  // We launch the HTTP request (don't await it)
  apiRequest<GeneratePrimaryPromptRes>(url, 'POST', true, req.body).catch(function (err) {
    // If the HTTP request fails, we reject the socket promise and clear the timeout
    const pending = pendingPromptGenerations.get(req.body.brandId);
    if (pending != null) {
      clearTimeout(pending.timeoutId);
      pending.reject(err);
      pendingPromptGenerations.delete(req.body.brandId);
    }
  });

  // We return the promise that will resolve when the socket event arrives
  return socketPromise;
}

export async function exportArticleById(
  id: string,
  primaryKeyword: string,
  kind: ExportKind,
  isDownload: boolean
) {
  const req = exportArticleReqSchema.parse({
    params: { id },
    query: null,
    // TODO: when pdf, we will need to do it (probably) on the backend
    //       for now, it is easier to do it on the frontend
    // body: { kind, isDownload },
    body: { kind, isDownload: false },
  });

  const result = await apiRequest<ExportArticleRes>(
    `/article/${req.params.id}/export`,
    'POST',
    true,
    req.body
  );
  if (!isDownload) {
    return result;
  }

  let mime = 'text/plain';
  let ext = 'txt';
  switch (kind) {
    case ExportKind.Markdown:
      mime = 'text/markdown';
      ext = 'md';
      break;
    default:
      mime = 'text/plain';
      ext = 'txt';
  }

  const fileName = dasherize(primaryKeyword);

  const blob = new Blob([result.data], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName ?? `Article_${id}`}.${ext}`);
  document.body.appendChild(link);
  link.click();

  return true;
}

export async function exportArticleToIntegration(
  id: ExportArticleIntegrationReq['params']['id'],
  body: ExportArticleIntegrationReq['body']
) {
  const req = exportArticleIntegrationReqSchema.parse({ params: { id }, body });
  return apiRequest<ExportArticleIntegrationRes>(
    `/article/${req.params.id}/export/integration`,
    'POST',
    true,
    req.body
  );
}

export async function generateOneClick(data: GenerateOneClickReq['body']) {
  const req = generateOneClickReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  return apiRequest<GenerateOneClickRes>(`/article/generate-one-click`, 'POST', true, req.body);
}

export async function refreshContent(data: RefreshContentReq['body']) {
  const req = refreshContentReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  return apiRequest<RefreshContentRes>(`/article/refresh`, 'POST', true, req.body);
}
